import {
  applyEntitySchema,
  objectSchema,
  optionalTolerant,
  sourceJson,
  wireValue,
} from '../../_utils/fcdc-v2'
import { ExpiryReminderType } from './expiry-reminder'
import { LengthPriceCap, Price } from './price-cap'
import { PriceSlots } from './price-slots'
import { Rate } from './rate'
import { SessionPriceAlgorithm } from './session-price-algorithm'
import { SharedAccess } from './shared-access'
import {
  parseRegularRateAfterExpiry,
  RegularRateAfterExpiry,
} from './regular-rate-after-expiry'
import {
  defaultSlotRefundMode,
  parseSlotRefundModeType,
  UnusedTimeRefund,
} from './unused-time-refund'
import { UnlockPrice } from './unlock-price'
import { Duration } from './settings'
import * as z from 'zod'

const pricingSnapshotSchema = objectSchema({
  sessionPriceAlgorithm: optionalTolerant(z.unknown()),
  lockerBaseRates: optionalTolerant(z.array(z.unknown())),
  sessionLengthPriceCap: optionalTolerant(z.unknown()),
  sessionOverdueLengthPriceCap: optionalTolerant(z.unknown()),
  sessionPriceSlots: optionalTolerant(z.unknown()),
  serviceFee: optionalTolerant(z.array(z.unknown())),
  depositionPrice: optionalTolerant(z.array(z.unknown())),
  unlockPrices: optionalTolerant(z.array(z.unknown())),
  expiryReminder: optionalTolerant(z.unknown()),
  sessionSharedAccess: optionalTolerant(z.unknown()),
  unusedTimeRefund: optionalTolerant(z.unknown()),
  regularRateAfterExpiry: optionalTolerant(
    objectSchema({
      enabled: optionalTolerant(z.boolean()),
    })
  ),
  vat: optionalTolerant(z.union([z.number(), z.string()])),
})

export type PricingSnapshotExpiryReminder =
  | {
      type: ExpiryReminderType.Free
      durationUntilExpiry: Duration
    }
  | {
      type: ExpiryReminderType.Paid
      durationUntilExpiry: Duration
      prices: Price[]
    }

/** Pricing settings frozen at scan or booking creation (see API `PricingSnapshot`). */
export class PricingSnapshot {
  sessionPriceAlgorithm?: SessionPriceAlgorithm
  lockerBaseRates?: Rate[]
  sessionLengthPriceCap?: LengthPriceCap
  sessionOverdueLengthPriceCap?: LengthPriceCap
  sessionPriceSlots?: PriceSlots
  serviceFee?: Price[]
  depositionPrice?: Price[]
  unlockPrices?: UnlockPrice[]
  expiryReminder?: PricingSnapshotExpiryReminder
  sessionSharedAccess?: SharedAccess
  unusedTimeRefund?: UnusedTimeRefund
  regularRateAfterExpiry?: RegularRateAfterExpiry
  vat?: number

  constructor(json: any) {
    if (!json) {
      return
    }

    const source = sourceJson(json) as Record<string, unknown>
    const plain = wireValue(source) as Record<string, unknown>
    this.sessionPriceAlgorithm = plain['sessionPriceAlgorithm']
      ? SessionPriceAlgorithm.getFactory().make(plain['sessionPriceAlgorithm'])
      : undefined
    this.lockerBaseRates = Array.isArray(plain['lockerBaseRates'])
      ? plain['lockerBaseRates'].map(rate => new Rate(rate))
      : undefined
    this.sessionLengthPriceCap = plain['sessionLengthPriceCap']
      ? new LengthPriceCap(plain['sessionLengthPriceCap'])
      : undefined
    this.sessionOverdueLengthPriceCap = plain['sessionOverdueLengthPriceCap']
      ? new LengthPriceCap(plain['sessionOverdueLengthPriceCap'])
      : undefined
    this.sessionPriceSlots = plain['sessionPriceSlots']
      ? new PriceSlots(plain['sessionPriceSlots'])
      : undefined
    this.serviceFee = Array.isArray(plain['serviceFee'])
      ? plain['serviceFee'].map(price => new Price(price))
      : undefined
    this.depositionPrice = Array.isArray(plain['depositionPrice'])
      ? plain['depositionPrice'].map(price => new Price(price))
      : undefined
    this.unlockPrices = Array.isArray(plain['unlockPrices'])
      ? plain['unlockPrices'].map(unlockPrice =>
          UnlockPrice.getFactory().make(unlockPrice)
        )
      : undefined
    this.expiryReminder = parsePricingSnapshotExpiryReminder(
      plain['expiryReminder']
    )
    this.sessionSharedAccess = plain['sessionSharedAccess'] as
      | SharedAccess
      | undefined
    this.regularRateAfterExpiry = parseRegularRateAfterExpiry(
      plain['regularRateAfterExpiry']
    )
    const vat = plain['vat']
    this.vat = vat !== undefined && vat !== null ? Number(vat) : undefined

    this.unusedTimeRefund = parsePricingSnapshotUnusedTimeRefund(
      plain['unusedTimeRefund']
    )
    applyEntitySchema(this, pricingSnapshotSchema, plain, {})
  }
}

function parsePricingSnapshotUnusedTimeRefund(
  json: unknown
): UnusedTimeRefund | undefined {
  if (!json || typeof json !== 'object') {
    return undefined
  }

  const record = json as Record<string, unknown>
  if (record['enabled'] === undefined) {
    return undefined
  }

  const slotRefundModeJson = record['slotRefundMode']
  const slotRefundMode =
    slotRefundModeJson &&
    typeof slotRefundModeJson === 'object' &&
    slotRefundModeJson !== null &&
    'type' in slotRefundModeJson
      ? {
          type: parseSlotRefundModeType(
            (slotRefundModeJson as { type: unknown }).type
          ),
        }
      : defaultSlotRefundMode()

  return {
    enabled: Boolean(record['enabled']),
    slotRefundMode,
  }
}

function parsePricingSnapshotExpiryReminder(
  json: any
): PricingSnapshotExpiryReminder | undefined {
  if (!json) {
    return undefined
  }

  if (json.type === ExpiryReminderType.Paid) {
    return {
      ...json,
      type: json.type,
      durationUntilExpiry: json.durationUntilExpiry,
      prices: (json.prices ?? []).map((price: any) => new Price(price)),
    }
  }

  if (json.type === ExpiryReminderType.Free) {
    return {
      ...json,
      type: json.type,
      durationUntilExpiry: json.durationUntilExpiry,
    }
  }

  return { ...json }
}

export function parsePricingSnapshot(json: any): PricingSnapshot | undefined {
  if (!json) {
    return undefined
  }
  return new PricingSnapshot(json)
}
